import * as monaco from 'monaco-editor';
import { isScriptPath, languageOf } from './projectPaths';
import { SANDBOX_MONACO_TYPECHECK_OPTIONS, buildAmbientModules, shouldSuppressDiagnostic, unwrapDeclareModule } from '@gotti/sandbox-core';
import { ASSET_MANAGER_DTS, buildAssetPathsDts } from '../assets/assetTypes';

// Import the ACTUAL type declarations from the built package dist files
import coreSystemDts from '../../../../packages/core/dist/base/System.d.ts?raw';
import coreEntityDts from '../../../../packages/core/dist/Entity.d.ts?raw';
import coreComponentDts from '../../../../packages/core/dist/Component.d.ts?raw';
import coreTypesDts from '../../../../packages/core/dist/types.d.ts?raw';
import clientSystemDts from '../../../../packages/client/dist/ClientSystem.d.ts?raw';
import iClientSystemDts from '../../../../packages/client/dist/IClientSystem.d.ts?raw';
import serverSystemDts from '../../../../packages/server/dist/ServerSystem.d.ts?raw';
import iServerSystemDts from '../../../../packages/server/dist/IServerSystem.d.ts?raw';
// The real @gotti/sandbox-test package's built .d.ts — the test-authoring API
// specs import. Drives the `@gotti/sandbox-test` ambient module below.
import testRuntimeDts from '../../../../packages/sandbox-test/dist/engine/testRuntime.d.ts?raw';
import sandboxTestDts from '../../../../packages/sandbox-test/dist/engine/sandboxTest.d.ts?raw';
import canvasProbeDts from '../../../../packages/sandbox-test/dist/canvasProbe.d.ts?raw';

// Build the Monaco type declarations using the shared builder from
// @gotti/sandbox-core — same logic the backend uses to produce identical
// ambient module declarations.
const { coreModule: coreModuleRaw, clientModule, serverModule, testModule, playgroundModule, deployerModule } =
  buildAmbientModules({
    core: {
      types: coreTypesDts,
      system: coreSystemDts,
      entity: coreEntityDts,
      component: coreComponentDts,
    },
    client: { clientSystem: clientSystemDts, iClientSystem: iClientSystemDts },
    server: { serverSystem: serverSystemDts, iServerSystem: iServerSystemDts },
    sandboxTest: { testRuntime: testRuntimeDts, sandboxTest: sandboxTestDts, canvasProbe: canvasProbeDts },
  });

const GOTTI_CORE_MODULE = unwrapDeclareModule(coreModuleRaw) + '\n' + ASSET_MANAGER_DTS;
const GOTTI_CLIENT_MODULE = unwrapDeclareModule(clientModule);
const GOTTI_SERVER_MODULE = unwrapDeclareModule(serverModule);
// `@gotti/sandbox-test` is the test-authoring API specs import. Registering its
// declaration (built from the real package .d.ts) is what makes
// `import { ... } from '@gotti/sandbox-test'` resolve in the editor.
const GOTTI_TEST_MODULE = unwrapDeclareModule(testModule);

let monacoConfigured = false;

export function configureMonaco() {
  if (monacoConfigured) return;
  monacoConfigured = true;

  // Sync ALL models to the TypeScript worker — not just the active one.
  // Without this, `declare module` augmentations in one file (e.g.
  // InputSystem extending ClientApiRegistry) are invisible to other
  // files (e.g. PredictionSystem reading this.$api.inputBuffer).
  monaco.languages.typescript.typescriptDefaults.setEagerModelSync(true);

  // Use the shared type-check options from @gotti/sandbox-core — the single
  // source of truth, behaviourally matched by the backend TypeCheckTool. The
  // Monaco variant sets `baseUrl: 'file:///'` so the `@/` path alias resolves
  // against Monaco's `file:///…` model URIs (with `baseUrl: '/'` the alias
  // produced scheme-less candidates that never matched a model → TS2307 on
  // every `@/` import). The raw numeric enum values match Monaco's typescript
  // enum values exactly.
  monaco.languages.typescript.typescriptDefaults.setCompilerOptions(
    SANDBOX_MONACO_TYPECHECK_OPTIONS as unknown as monaco.languages.typescript.CompilerOptions,
  );

  monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
    noSemanticValidation: false,
    noSyntaxValidation: false,
    noSuggestionDiagnostics: false,
  });

  // Register @gotti/* modules from built .d.ts files
  monaco.languages.typescript.typescriptDefaults.addExtraLib(
    GOTTI_CORE_MODULE,
    'file:///node_modules/@gotti/core/index.d.ts'
  );
  monaco.languages.typescript.typescriptDefaults.addExtraLib(
    GOTTI_CLIENT_MODULE,
    'file:///node_modules/@gotti/client/index.d.ts'
  );
  monaco.languages.typescript.typescriptDefaults.addExtraLib(
    GOTTI_SERVER_MODULE,
    'file:///node_modules/@gotti/server/index.d.ts'
  );
  monaco.languages.typescript.typescriptDefaults.addExtraLib(
    GOTTI_TEST_MODULE,
    'file:///node_modules/@gotti/sandbox-test/index.d.ts'
  );
  // A project's root playground and gotti config files import these (Phase 3c-2: the editor holds the whole tree).
  monaco.languages.typescript.typescriptDefaults.addExtraLib(
    unwrapDeclareModule(playgroundModule),
    'file:///node_modules/@gotti/playground/index.d.ts'
  );
  monaco.languages.typescript.typescriptDefaults.addExtraLib(
    unwrapDeclareModule(deployerModule),
    'file:///node_modules/@gotti/deployer/index.d.ts'
  );
  refreshAssetPathTypes([]);

  // Expose the monaco instance for e2e/debugging (read model markers,
  // diagnostics). Harmless in prod — monaco is already loaded in the page.
  (window as any).monaco = monaco;

  console.log('[Sandbox] Registered @gotti type declarations from package dist');
}

/** Makes a file's Monaco model at `file:///<tree path>` in its language — reusing one Monaco already holds for that URI. */
export const monacoModelFactory = (path: string, code: string): monaco.editor.ITextModel => {
  const uri = monaco.Uri.parse(`file:///${path}`);
  const existing = monaco.editor.getModel(uri);
  if (existing) {
    if (existing.getValue() !== code) existing.setValue(code);
    return existing;
  }
  return monaco.editor.createModel(code, languageOf(path), uri);
};

/** A script file's diagnostics from the TypeScript worker, filtered as the file tree shows them; another file has none. */
export async function validateFile(models: Map<string, monaco.editor.ITextModel>, path: string): Promise<string[]> {
  const model = models.get(path);
  if (!model || model.isDisposed() || !isScriptPath(path)) return [];
  const worker = await monaco.languages.typescript.getTypeScriptWorker();
  const client = await worker(model.uri);
  const [syntactic, semantic] = await Promise.all([
    client.getSyntacticDiagnostics(model.uri.toString()),
    client.getSemanticDiagnostics(model.uri.toString()),
  ]);
  // Flatten diagnostic message chains (as ts.flattenDiagnosticMessageText does).
  const flatten = (text: string | { messageText: string; next?: any[] }): string =>
    typeof text === 'string' ? text : [text.messageText, ...(text.next ?? []).map(flatten)].join('\n');
  // shouldSuppressDiagnostic filters @gotti-specific noise and lets real errors in the project's own files through.
  return [...syntactic, ...semantic]
    .map((d) => ({ code: d.code, message: flatten(d.messageText) }))
    .filter((d) => !shouldSuppressDiagnostic(d.code, d.message))
    .map((d) => d.message);
}

const ASSET_PATHS_LIB_URI = 'file:///node_modules/@gotti/core/asset-paths.d.ts';

/** Re-register the asset-path registry augmentation so autocomplete reflects the
 *  current /assets contents. Safe to call repeatedly (same URI replaces). */
export function refreshAssetPathTypes(paths: string[]): void {
  monaco.languages.typescript.typescriptDefaults.addExtraLib(buildAssetPathsDts(paths), ASSET_PATHS_LIB_URI);
}
